import { ADVANCED_EXPORT_REPORTS, ADVANCED_EXPORTS_ROLE_LABEL } from '../../data/advancedExports'

import type { JourneyCoreState, JourneySpec, JourneyStep } from '../../journey'

export type AdvancedExportsView = 'reports' | 'loading' | 'users' | 'permissions' | 'advanced' | 'excel'

/** What the right-hand column emphasises: the permission gate, or one report. */
export type AdvancedExportsSpotlight = 'pending' | 'satisfied' | number

export interface AdvancedExportsState extends JourneyCoreState {
  view: AdvancedExportsView
  annotationVisible: boolean
  permissionChecked: boolean
  saving: boolean
  savedToast: boolean
  navActive: 'reportes' | 'usuarios'
  tabInserted: boolean
  selectedReport: number | null
  previewLoading: boolean
  generating: boolean
  downloadToast: boolean
  spotlight: AdvancedExportsSpotlight
}

const INITIAL: AdvancedExportsState = {
  view: 'reports',
  caption: 'La pestaña de Exportación Avanzada no está visible',
  target: null,
  cursorVisible: true,
  clicking: false,
  clickSeq: 0,
  annotationVisible: false,
  permissionChecked: false,
  saving: false,
  savedToast: false,
  navActive: 'reportes',
  tabInserted: false,
  selectedReport: null,
  previewLoading: false,
  generating: false,
  downloadToast: false,
  spotlight: 'pending',
}

const describe = (index: number) => {
  const report = ADVANCED_EXPORT_REPORTS[index]

  return `${report.title}: ${report.description}`
}

const build = (): JourneyStep<AdvancedExportsState>[] => {
  const steps: JourneyStep<AdvancedExportsState>[] = [
    // Act 1 — the tab is simply not there. Without the role the trigger is
    // never rendered, so there is nothing to click: the cursor scans the row
    // and an annotation names the absence.
    { at: 700, patch: { target: 'tabRow' } },
    { at: 1900, patch: { target: 'tabSlot' } },
    { at: 2900, patch: { annotationVisible: true, caption: 'Solo aparecen Stock, Ventas y Margen' } },

    // Act 2 — an administrator grants the permission.
    { at: 4300, patch: { target: 'navUsuarios' } },
    {
      at: 5600,
      click: true,
      patch: { navActive: 'usuarios', caption: 'Usuarios: se edita el perfil del vendedor' },
    },
    { at: 5900, patch: { view: 'loading' } },
    { at: 6600, patch: { view: 'users' } },
    { at: 7000, patch: { target: 'editUser' } },
    { at: 8400, click: true, patch: {} },
    { at: 8700, patch: { view: 'loading' } },
    { at: 9400, patch: { view: 'permissions', caption: 'Permisos del perfil de Ana Pérez' } },
    { at: 9900, patch: { target: 'permissionBox' } },
    {
      at: 11300,
      click: true,
      patch: { permissionChecked: true, caption: `${ADVANCED_EXPORTS_ROLE_LABEL}: habilitado` },
    },
    { at: 12000, patch: { target: 'save' } },
    { at: 13200, click: true, patch: { saving: true } },
    { at: 13700, patch: { saving: false, savedToast: true, spotlight: 'satisfied' } },

    // Act 3 — the tab appears, first in the row and already selected.
    { at: 14200, patch: { target: 'navReportes' } },
    { at: 15400, click: true, patch: { navActive: 'reportes' } },
    { at: 15600, patch: { view: 'loading' } },
    {
      at: 16200,
      patch: {
        view: 'advanced',
        tabInserted: true,
        caption: 'Aparece primero en la fila y queda seleccionada',
      },
    },

    // Act 4 — one report end to end, including the generated file.
    { at: 17200, patch: { target: 'card0' } },
    {
      at: 18400,
      click: true,
      patch: { selectedReport: 0, spotlight: 0, previewLoading: true, caption: describe(0) },
    },
    { at: 19400, patch: { previewLoading: false } },
    { at: 20600, patch: { target: 'download' } },
    { at: 22000, click: true, patch: { caption: 'Descargando planilla de Excel' } },
    { at: 22300, patch: { view: 'excel', generating: true, cursorVisible: false } },
    {
      at: 24100,
      patch: { generating: false, caption: 'Encabezado congelado, columnas ajustadas, filtros listos' },
    },
    { at: 25000, patch: { downloadToast: true } },
  ]

  // Act 5 — the remaining four, three seconds each: select and preview only.
  ;[1, 2, 3, 4].forEach((index, n) => {
    const at = 27000 + n * 3000

    steps.push({
      at,
      patch: {
        view: 'advanced',
        cursorVisible: true,
        generating: false,
        downloadToast: false,
        target: `card${index}`,
      },
    })
    steps.push({
      at: at + 900,
      click: true,
      patch: {
        selectedReport: index,
        spotlight: index,
        previewLoading: true,
        caption: describe(index),
      },
    })
    steps.push({ at: at + 1600, patch: { previewLoading: false } })
    steps.push({ at: at + 2100, patch: { target: 'download' } })
  })

  steps.push({ at: 39200, patch: { caption: 'Cinco reportes, un permiso', spotlight: 'satisfied' } })

  return steps
}

export const advancedExportsSpec: JourneySpec<AdvancedExportsState> = {
  initial: INITIAL,
  loopMs: 40_000,
  build,
}
